import { get } from 'svelte/store';

import { real_id } from 'src/helpers/interface_helpers';
import { getXzComponentId, xzRequest } from 'src/services/xz_http_reqs';
import { devices_settings, devices_status } from 'src/stores';
import type { Device } from 'src/types/Device';

const logWaveCover = (stage: string, data: Record<string, unknown> = {}) => {
	console.log(`[zwave][cover] ${stage}`, data);
};

export const getWaveCoverComponentKey = (device: Device): string => {
	return `cover:${getXzComponentId(device)}`;
};

export const getWaveCoverComponentId = (device: Device): number => {
	const settings = get(devices_settings)?.[real_id(device.id)] || {};
	const status = devices_status.getByRealId(device.id) || {};
	const fromSettings = Object.keys(settings).find((key) => key.startsWith('cover:'));
	const fromStatus = Object.keys(status).find((key) => key.startsWith('cover:'));
	const existingKey = fromSettings || fromStatus;
	if (existingKey) {
		const id = Number(existingKey.split(':')[1]);
		if (Number.isFinite(id)) {
			return id;
		}
	}
	return getXzComponentId(device);
};

export const mergeWaveCoverConfig = (
	deviceId: string,
	componentId: number,
	config: Record<string, unknown>,
): void => {
	const id = real_id(deviceId);
	const existing = get(devices_settings)?.[id] || {};
	const key = `cover:${componentId}`;
	devices_settings.update_devices_settings(id, {
		...existing,
		[key]: {
			...(existing[key] || {}),
			id: componentId,
			...config,
		},
	});
};

export const readWaveCoverConfig = (device: Device): Record<string, unknown> => {
	const id = real_id(device.id);
	const componentId = getWaveCoverComponentId(device);
	return get(devices_settings)?.[id]?.[`cover:${componentId}`] || {};
};

/**
 * Cover.GetConfig via XZ and merge into devices_settings[cover:id].
 */
export const zwaveCoverGetConfig = async (device: Device) => {
	const componentId = getWaveCoverComponentId(device);
	const response = await xzRequest(device, 'Cover.GetConfig', { id: componentId });

	if (response?.isok) {
		const config =
			(response.data && typeof response.data === 'object' ? response.data : null) ||
			(response.result && typeof response.result === 'object' ? response.result : null) ||
			{};
		mergeWaveCoverConfig(device.id, componentId, config as Record<string, unknown>);
		logWaveCover('get_config_ok', { deviceId: device.id, componentId });
	} else {
		logWaveCover('get_config_failed', { deviceId: device.id, componentId, response });
	}

	return response;
};

/**
 * Cover.SetConfig via XZ and merge successful config patch into store.
 */
export const zwaveCoverSetConfig = async (device: Device, config: Record<string, unknown>) => {
	const componentId = getWaveCoverComponentId(device);
	const response = await xzRequest(device, 'Cover.SetConfig', {
		id: componentId,
		config,
	});

	if (response?.isok) {
		mergeWaveCoverConfig(device.id, componentId, config);
	}

	return response;
};

export const zwaveCoverCalibrate = async (device: Device) => {
	const componentId = getWaveCoverComponentId(device);
	return xzRequest(device, 'Cover.Calibrate', { id: componentId });
};

/**
 * One-shot Cover.GetConfig when opening ZW-COVER settings.
 */
export const ensureWaveCoverSettings = async (device: Device): Promise<Record<string, unknown>> => {
	if (device.type !== 'ZW-COVER') {
		return {};
	}

	const id = real_id(device.id);
	const status = devices_status.getByRealId(id);
	if (status?.online === false) {
		logWaveCover('skip_offline', { deviceId: id });
		return readWaveCoverConfig(device);
	}

	await zwaveCoverGetConfig(device);
	return readWaveCoverConfig(device);
};
